import Select from 'react-select';
import type { CodeDefinition } from '../../../js/api/models/CodeDefinition';
import { Checkbox } from '../components/checkbox';
import { Field, Label, Fieldset, Legend } from '../components/fieldset';
import { Input } from '../components/input';
import { reactSelectReset } from './utils';

export type FieldType = 'text' | 'number' | 'select' | 'multiselect' | 'boolean';

export type UnitType = 'hours' | '%';

export interface DynamicField {
  field_name: string;
  field_title: string;
  field_type: FieldType;
  field_order: number;
  field_required: boolean;
  field_unit: UnitType;
  choices_category: string;
  field_value: any;
  choices?: CodeDefinition[];
}

interface DynamicFieldFactoryProps {
  fields: DynamicField[];
  onChange: (fieldName: string, value: any) => void;
  title?: string;
  readOnly?: boolean;
  columns?: number;
  menuPortalTarget?: HTMLElement | null;
}

export function DynamicFieldFactory({
  fields,
  onChange,
  title,
  readOnly = false,
  columns = 1,
  menuPortalTarget,
}: DynamicFieldFactoryProps) {
  // Sort fields based on mode
  const sortedFields = [...fields].sort((a, b) => {
    if (readOnly) {
      // In readOnly mode, just sort by field_order
      return a.field_order - b.field_order;
    } else {
      // In edit mode, put boolean fields last while maintaining relative order
      if (a.field_type === 'boolean' && b.field_type !== 'boolean') return 1;
      if (a.field_type !== 'boolean' && b.field_type === 'boolean') return -1;
      return a.field_order - b.field_order;
    }
  });

  // Split fields into columns (top to bottom, then left to right)
  const colCount = Math.max(1, columns);
  const fieldsPerCol = Math.ceil(sortedFields.length / colCount);
  const columnsArr: DynamicField[][] = Array.from({ length: colCount }, (_, i) =>
    sortedFields.slice(i * fieldsPerCol, (i + 1) * fieldsPerCol)
  );

  // Helper to render field value in read-only mode
  // eslint-disable-next-line sonarjs/cognitive-complexity
  function renderReadOnlyValue(field: DynamicField) {
    if (field.field_type === 'multiselect') {
      if (!Array.isArray(field.field_value) || field.field_value.length === 0) return '--';
      // Render as comma-separated string
      const values = field.field_value.map((val: string) => {
        const match = field.choices?.find((choice) => choice.code === val);
        return match ? match.title : val;
      });
      return values.join(', ');
    }
    if (field.field_value !== undefined && field.field_value !== null && field.field_value !== '') {
      if (field.choices && field.choices.length && typeof field.field_value === 'string') {
        return (
          field.choices.find((choice) => choice.code === field.field_value)?.title ||
          field.field_value
        );
      }
      if (typeof field.field_value === 'boolean') {
        return field.field_value ? 'Yes' : 'No';
      }
      if (field.field_type === 'number' && typeof field.field_value === 'number') {
        return field.field_value.toLocaleString();
      }
      return field.field_value;
    }
    return '--';
  }

  if (readOnly) {
    return (
      <Fieldset>
        {title && <Legend>{title}</Legend>}
        <div className={`tw:flex tw:gap-8`}>
          {columnsArr.map((colFields, colIdx) => (
            <div key={colIdx} className="tw:flex-1 tw:space-y-4">
              {colFields.map((field) => (
                // inline display
                // <div key={field.field_name} className="tw:flex tw:justify-between tw:items-center">
                // stacked display
                <div key={field.field_name}>
                  <div className="tw:text-zinc-400 tw:text-sm">{field.field_title}</div>
                  <div className="tw:text-zinc-700 tw:dark:text-zinc-300">
                    {renderReadOnlyValue(field)}
                    {field.field_unit && (
                      <span className="tw:text-zinc-500 tw:ml-1">{field.field_unit}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </Fieldset>
    );
  }

  const selectStyles = menuPortalTarget
    ? { ...reactSelectReset, menuPortal: (base: any) => ({ ...base, zIndex: 70 }) }
    : reactSelectReset;

  const renderField = (field: DynamicField) => {
    const commonProps = {
      name: field.field_name,
      required: field.field_required,
      value: field.field_value,
      onChange: (value: any) => onChange(field.field_name, value),
    };

    switch (field.field_type) {
      case 'text':
        return (
          <Field key={field.field_name}>
            <Label>
              {field.field_title}
              {field.field_required && <span className="tw:text-stoplight-danger">*</span>}
              {field.field_unit && (
                <span className="tw:text-zinc-500 tw:ml-1">({field.field_unit})</span>
              )}
            </Label>
            <Input
              name={field.field_name}
              required={field.field_required}
              value={field.field_value ?? ''}
              type="text"
              onChange={(e) => onChange(field.field_name, e.target.value)}
            />
          </Field>
        );

      case 'number':
        return (
          <Field key={field.field_name}>
            <Label>
              {field.field_title}
              {field.field_required && <span className="tw:text-stoplight-danger">*</span>}
              {field.field_unit && (
                <span className="tw:text-zinc-500 tw:ml-1">({field.field_unit})</span>
              )}
            </Label>
            <Input
              name={field.field_name}
              value={field.field_value ?? ''}
              type="number"
              onChange={(e) =>
                onChange(field.field_name, e.target.value === '' ? '' : e.target.value)
              }
            />
          </Field>
        );

      case 'select':
        return (
          <Field key={field.field_name}>
            <Label>
              {field.field_title}
              {field.field_required && <span className="tw:text-stoplight-danger">*</span>}
              {field.field_unit && (
                <span className="tw:text-zinc-500 tw:ml-1">({field.field_unit})</span>
              )}
            </Label>
            <Select
              styles={selectStyles}
              menuPortalTarget={menuPortalTarget}
              name={field.field_name}
              options={
                field.choices?.map((choice) => ({
                  value: choice.code,
                  label: choice.title,
                })) || []
              }
              value={
                field.choices?.find((choice) => choice.code === field.field_value)
                  ? {
                      value: field.field_value,
                      label:
                        field.choices.find((choice) => choice.code === field.field_value)?.title ||
                        '',
                    }
                  : null
              }
              onChange={(option) => onChange(field.field_name, option?.value ?? '')}
              placeholder="Select an option"
              className="tw:mt-1"
              required={field.field_required}
            />
          </Field>
        );

      case 'multiselect':
        return (
          <Field key={field.field_name}>
            <Label>
              {field.field_title}
              {field.field_required && <span className="tw:text-stoplight-danger">*</span>}
              {field.field_unit && (
                <span className="tw:text-zinc-500 tw:ml-1">({field.field_unit})</span>
              )}
            </Label>
            <Select
              isMulti
              styles={selectStyles}
              menuPortalTarget={menuPortalTarget}
              name={field.field_name}
              options={
                field.choices?.map((choice) => ({
                  value: choice.code,
                  label: choice.title,
                })) || []
              }
              value={
                field.choices
                  ?.filter((choice) =>
                    Array.isArray(field.field_value)
                      ? field.field_value.includes(choice.code)
                      : field.field_value === choice.code
                  )
                  .map((choice) => ({
                    value: choice.code,
                    label: choice.title,
                  })) || []
              }
              onChange={(selected) => {
                const values = selected ? selected.map((option) => option.value) : [];
                onChange(field.field_name, values);
              }}
              className="tw:mt-1"
              required={field.field_required}
            />
          </Field>
        );

      case 'boolean':
        return (
          <Field key={field.field_name} className="tw:flex tw:items-center tw:space-x-3">
            <Checkbox {...commonProps} color="brandgreen" checked={!!field.field_value} />
            <Label>
              {field.field_title}
              {field.field_required && <span className="tw:text-stoplight-danger">*</span>}
              {field.field_unit && (
                <span className="tw:text-zinc-500 tw:ml-1">({field.field_unit})</span>
              )}
            </Label>
          </Field>
        );

      default:
        return null;
    }
  };

  return (
    <Fieldset>
      {title && <Legend>{title}</Legend>}
      <div className={`tw:flex tw:gap-8`}>
        {columnsArr.map((colFields, colIdx) => (
          <div key={colIdx} className="tw:flex-1 tw:space-y-6">
            {colFields.map(renderField)}
          </div>
        ))}
      </div>
    </Fieldset>
  );
}
